import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from 'react-router-dom';
import { Box, Grid, IconButton } from "@mui/material";
import VisibilityIcon from '@mui/icons-material/Visibility';

import { useTranslation } from "react-i18next";
import DataTable from "../../components/common/DataTable";
import { AuthContext } from "../../context/AuthContext";
import { genericGet } from "../../services/apiServices";
import { GET_IMPORT_SHIPMENT_COSTING } from "../../services/apiRoutes";
import CustomReport from "../common/CustomReport";
import { useCompanyDateFormat } from "../../constants/useCompanyDateFormat";
import { getColorById, getColorPlate, getSuppliersList } from "../../services/CommonServices";
import CustomFilter from "../common/CustomFilter";
import CustomAutocomplete from "../common/CustomAutoComplete";
import CustomButton from "../common/CustomButton";
import { genericPost } from "../../services/authServices";
import FindReplaceOutlinedIcon from '@mui/icons-material/FindReplaceOutlined';
import { ClearRounded, } from "@mui/icons-material";
import CustomDataTable from "../common/CustomDataTable";
import { AccessDenied, hasCreatePermission, hasDeletePermission, hasEditPermission, hasViewPermission } from "../../services/CommonServices";
import { useLocation } from "react-router-dom";
import CustomTextField from "../common/CustomTextField";
import CustomStatus from "../common/CustomStatus";


const title = "Import Shipment Costing Report";

const ImportShipmentCostingReport = () => {
    const { t, i18n } = useTranslation();
    const companyDateFormat = useCompanyDateFormat();
    const [permission, setPermissions] = useState()
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const { token } = useContext(AuthContext)
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const filterOpen = () => setOpenDrawer(true);
    const filterClose = () => setOpenDrawer(false);
    const [openDrawer, setOpenDrawer] = useState(false);
    const [suppliersList, setSuppliersList] = useState([]);
    const [page, setPage] = useState(0); // UI 0-based
    const [pageSize, setPageSize] = useState(10);
    const [rowCount, setRowCount] = useState(0);
    const [colorlist, setColorlist] = useState([]);
    const [filterPayload, setFilterPayload] = useState({
        supplierNamesId: '',
        fromDate: '',
        toDate: '',
    });
    const handleFilterChange = (field, value) => {
        setFilterPayload((prev) => ({
            ...prev,
            [field]: value || '',
        }));
    };
    const handleClearFilters = useMemo(() => {
        return () => {
            setFilterPayload({
                supplierNamesId: '',
                fromDate: '',
                toDate: '',
            });
            fetchData(0, pageSize, '');
        };
    }, [pageSize]);
    useEffect(() => {
        getCommonData();
    }, []);
    const location = useLocation();
    const currentPath = location.pathname;
    useEffect(() => {
        let rolePermisionsData = JSON.parse(sessionStorage.getItem('userData')) || []
        let editPermision = hasEditPermission(currentPath, rolePermisionsData?.permissions)
        let deletePermission = hasDeletePermission(currentPath, rolePermisionsData?.permissions)
        let createPermision = hasCreatePermission(currentPath, rolePermisionsData?.permissions)
        let viewPermision = hasViewPermission(currentPath, rolePermisionsData?.permissions)

        setPermissions({
            edit: editPermision,
            delete: deletePermission,
            create: createPermision,
            view: viewPermision,
        })
    }, [])


    const getCommonData = async () => {
        const suppliersListData = await getSuppliersList();
        setSuppliersList(suppliersListData);
        const colorData = await getColorPlate();
        setColorlist(colorData);

    }
    const columns = useMemo(
        () => [
            { accessorKey: "index", header: "SN.", size: 50, tableHeader: { alignItems: 'center', }, tableStyle: { textAlign: "center" }, enableColumnFilter: false },
            { accessorKey: 'etaDate', header: 'Date', size: 100, tableStyle: { textAlign: "center" }, },
            { accessorKey: 'shipmentId', header: 'Shipment Id', size: 150, tableStyle: { textAlign: "left" }, },
            { accessorKey: 'piNumber', header: 'PI No.', size: 150, tableStyle: { textAlign: "center" }, },
            { accessorKey: 'commercialInvoiceNo', header: 'CI No.', size: 180, tableStyle: { textAlign: "center" }, },
            { accessorKey: 'bookingNumber', header: 'Booking No.', size: 180, tableStyle: { textAlign: "center" }, },

            {
                accessorKey: 'clientCompanyName', header: 'Supplier Name', size: 150, tableHeader: {
                    justifyContent: 'flex-start',
                },
            },


            {
                accessorKey: "billingCountry",
                header: "Country",
                tableStyle: { textAlign: "center" },
                size: 50,
                Cell: ({ row }) => {
                    return (
                        <CustomStatus
                            title={row.original.billingCountry}
                            sx={{
                                backgroundColor: row.original.countryIdBackground,
                                color: row.original.countryIdColor,
                                border: `1px solid ${row.original.countryIdColor}`,
                                textAlign: "center",
                                display: "inline-block",
                            }}
                        />
                    );
                },
                enableSorting: false,
            },
            { accessorKey: 'numberOfContainers', header: 'No. Of Container', size: 150, tableStyle: { textAlign: "center" }, },
            { accessorKey: 'totalQty', header: 'Total Qty', size: 100, tableStyle: { textAlign: "center" }, },

            {
                accessorKey: 'totalExpensesInBase', header: 'Total Cost', size: 150, tableStyle: { textAlign: "center" },
            },

            { accessorKey: 'costPerUnit', header: 'Cost per Unit', size: 150, tableStyle: { textAlign: "center" }, },
            {
                accessorKey: "action",
                header: "Action",
                size: 50,
                Cell: ({ row }) => (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1px' }}>
                        <IconButton onClick={() => handleViewClick(row.original.commercialInvoiceId)}>
                            <VisibilityIcon />
                        </IconButton>
                    </div>
                ),
            },
        ],
        [permission]
    );
    const handleViewClick = (commercialInvoiceId) => {
        navigate(`/app/reports/importshipmentcostingreport/${commercialInvoiceId}`);
    };


    const fetchData = async (pageIndex = 0, pageSize = 10) => {
        try {
            const payload = {
                page: pageIndex + 1,
                limit: pageSize,
                ...filterPayload
            };
            setLoading(true);
            // const response = await genericGet({ url: GET_IMPORT_SHIPMENT_COSTING });
            const response = await genericPost(GET_IMPORT_SHIPMENT_COSTING, payload, {});
            // if (response.data.success && response.data.commercialInvExps.length > 0) {
            if (response?.data?.success) {

                const mappedData = response.data.commercialInvExps.map((invoiceData, index) => {
                    const currencySymbol = invoiceData?.currency?.currency_symbol || '';
                    const baseCurrencySymbol =
                        invoiceData?.importShipment?.company?.baseCurrencyData?.currency_symbol || '';

                    const totalQty = invoiceData?.particulars?.reduce((acc, item) => acc + Number(item.qty || 0), 0) || 0;
                    const proformaInvoiceNo = invoiceData?.ProformaInvoice?.proformaInvoiceNo || "";
                    const proformaAmount = invoiceData?.particulars?.reduce(
                        (sum, item) => sum + (Number(item.amount) || 0), 0
                    );
                    const totalVouchers = invoiceData.paymentVoucher?.length || 0;
                    // $$$$$$$$$$$$$$$$$$$$$$$$$
                    const totalConversionRate = invoiceData.paymentVoucher?.reduce((sum, item) => sum + (Number(item.conversionRate) || 0), 0) || 0;
                    const halfConversionRatePayment = totalConversionRate > 0 ? (totalConversionRate / totalVouchers).toFixed(2) : "";

                    const totalBooking = invoiceData.shippingBooking?.length || 0;
                    const totalBookingConversionRate = invoiceData.shippingBooking?.reduce((sum, item) => sum + (Number(item.conversionRate) || 0), 0) || 0;
                    const halfConversionRateBooking = totalBookingConversionRate > 0 ? (totalBookingConversionRate / totalBooking).toFixed(2) : "1";
                    const halfConversionRate =
                        Number(halfConversionRatePayment) > 0
                            ? Number(halfConversionRatePayment)
                            : Number(halfConversionRateBooking) > 0
                                ? Number(halfConversionRateBooking)
                                : 1;

                    const totalDutyLenght = invoiceData.duty?.length || 0;

                    const boeConversionRate = invoiceData?.duty?.reduce((sum, item) => {
                        return sum + (parseFloat(item.boeConversionRate || 0));
                    }, 0) || 0;

                    const totalBoeConversionRate = boeConversionRate > 0 ? (boeConversionRate / totalDutyLenght).toFixed(2) : 1;

                    const advanceAmount = invoiceData.advancePayment?.reduce(
                        (sum, item) => sum + (Number(item.referenceAmount) || 0), 0
                    ) || 0;

                    const advanceAmountBaseCurrency = invoiceData.advancePayment?.reduce(
                        (sum, item) => sum + (Number(item.amountInBaseCurrency) || 0), 0
                    ) || 0;


                    const finalAmount = invoiceData.finalPayment?.reduce(
                        (sum, item) => sum + (Number(item.referenceAmount) || 0), 0
                    ) || 0;


                    const finalAmountBaseCurrency = invoiceData.finalPayment?.reduce(
                        (sum, item) => sum + (Number(item.amountInBaseCurrency) || 0), 0
                    ) || 0;


                    const shippingLineCharges = invoiceData.shippingBooking?.reduce((sum, item) => sum + (Number(item.shippingLineCharges) || 0), 0) || 0;

                    const bankCharges = invoiceData.paymentVoucher?.reduce((sum, item) => sum + (Number(item.bankCharges) || 0), 0) || 0;

                    const totalDutyAmount = invoiceData?.duty?.reduce((sum, dutyItem) => {
                        return sum + (dutyItem?.particulars?.reduce((dutySum, p) => dutySum + parseFloat(p.duty || 0), 0) || 0);
                    }, 0) || 0;

                    const totalTaxAmount = invoiceData?.duty?.reduce((sum, dutyItem) => {
                        return sum + (dutyItem?.particulars?.reduce((taxSum, p) => taxSum + parseFloat(p.tax || 0), 0) || 0);
                    }, 0) || 0;

                    const totalExpenses = invoiceData?.expenseVoucher?.reduce((sum, item) => sum + (Number(item.amount) || 0), 0) || 0;

                    const totalExpensesInBase = invoiceData?.expenseVoucher?.reduce((sum, item) => sum + (Number(item.amountInBaseCurrency) || 0), 0) || 0;

                    const formatAmount = (amt, symbol) => `${symbol} ${Number(amt || 0).toFixed(2)}`;

                    const seaFreightChargesLedgerStatus = invoiceData?.expenseVoucher
                        ?.filter(item => item?.expenseLedger?.expenseLedgerStatus === 5)
                        ?.reduce((sum, item) => sum + Number(item.amount || 0), 0) || 0;

                    const seaFreightChargesLedgerBaseCurrency = invoiceData?.expenseVoucher
                        ?.filter(item => item?.expenseLedger?.expenseLedgerStatus === 5)
                        ?.reduce((sum, item) => sum + Number(item.amountInBaseCurrency || 0), 0) || 0;

                    const TransportCharges = invoiceData?.expenseVoucher
                        ?.filter(item => item?.expenseLedger?.expenseLedgerStatus === 4)
                        ?.reduce((sum, item) => sum + Number(item.amount || 0), 0) || 0;

                    const TransportChargesInBase = invoiceData?.expenseVoucher
                        ?.filter(item => item?.expenseLedger?.expenseLedgerStatus === 4)
                        ?.reduce((sum, item) => sum + Number(item.amountInBaseCurrency || 0), 0) || 0;

                    const CFSCharges = invoiceData?.expenseVoucher
                        ?.filter(item => item?.expenseLedger?.expenseLedgerStatus === 3)
                        ?.reduce((sum, item) => sum + Number(item.amount || 0), 0) || 0;

                    const CFSChargesInBase = invoiceData?.expenseVoucher
                        ?.filter(item => item?.expenseLedger?.expenseLedgerStatus === 3)
                        ?.reduce((sum, item) => sum + Number(item.amountInBaseCurrency || 0), 0) || 0;

                    const CHACharges = invoiceData?.expenseVoucher
                        ?.filter(item => item?.expenseLedger?.expenseLedgerStatus === 2)
                        ?.reduce((sum, item) => sum + Number(item.amount || 0), 0) || 0;

                    const CHAChargesInBase = invoiceData?.expenseVoucher
                        ?.filter(item => item?.expenseLedger?.expenseLedgerStatus === 2)
                        ?.reduce((sum, item) => sum + Number(item.amountInBaseCurrency || 0), 0) || 0;

                    const ShippingLineandAgencyCharges = invoiceData?.expenseVoucher
                        ?.filter(item => item?.expenseLedger?.expenseLedgerStatus === 1)
                        ?.reduce((sum, item) => sum + Number(item.amount || 0), 0) || 0;

                    const ShippingLineandAgencyChargesInBase = invoiceData?.expenseVoucher
                        ?.filter(item => item?.expenseLedger?.expenseLedgerStatus === 1)
                        ?.reduce((sum, item) => sum + Number(item.amountInBaseCurrency || 0), 0) || 0;

                    const miscellaneousExpenses = invoiceData?.expenseVoucher
                        ?.filter(item => item?.expenseLedger?.expenseLedgerStatus === 0)
                        ?.reduce((sum, item) => sum + Number(item.amount || 0), 0) || 0;

                    const miscellaneousExpensesInBase = invoiceData?.expenseVoucher
                        ?.filter(item => item?.expenseLedger?.expenseLedgerStatus === 0)
                        ?.reduce((sum, item) => sum + Number(item.amountInBaseCurrency || 0), 0) || 0;

                    const unitName = invoiceData?.particulars?.[0]?.productsUnit?.unitName || '';
                    const getCurrencySymbolsByStatus = (expenseVoucherList, status) => {
                        const currencyList = expenseVoucherList
                            ?.filter(item => item?.expenseLedger?.expenseLedgerStatus === status)
                            ?.map(item => item?.currency?.currency_symbol || '') || [];

                        const uniqueSymbols = [...new Set(currencyList)];
                        return uniqueSymbols.length === 1
                            ? uniqueSymbols[0]
                            : uniqueSymbols.join(', ');
                    };

                    const totalAmount = invoiceData?.particulars?.reduce((acc, item) => acc + (Number(item.qty || 0) * parseFloat(item.rate || 0)), 0) || 0;

                    const seaFreightSymbol = getCurrencySymbolsByStatus(invoiceData?.expenseVoucher, 5);
                    const transportChargesSymbol = getCurrencySymbolsByStatus(invoiceData?.expenseVoucher, 4);
                    const cfsChargesSymbol = getCurrencySymbolsByStatus(invoiceData?.expenseVoucher, 3);
                    const chaChargesSymbol = getCurrencySymbolsByStatus(invoiceData?.expenseVoucher, 2);
                    const shippingLineAgencySymbol = getCurrencySymbolsByStatus(invoiceData?.expenseVoucher, 1);

                    const currencyTaxSymbol = invoiceData?.duty?.[0]?.particulars?.[0]?.currencyTaxList?.currency_symbol || '';
                    const currencyDutySymbol = invoiceData?.duty?.[0]?.particulars?.[0]?.currencyDutyList?.currency_symbol || '';
                    let totalTaxAmountInBase = 0;
                    let totalDutyAmountInBase = 0;
                    let taxBaseCurrency = 0;
                    let dutyBaseCurrency = 0;
                    const matchBaseCurrency = parseInt(invoiceData?.importShipment?.company?.baseCurrency || '');
                    const matchTaxCurrency = parseInt(invoiceData?.duty?.[0]?.particulars?.[0]?.currencyTax || '');
                    const matchDutyCurrency = parseInt(invoiceData?.duty?.[0]?.particulars?.[0]?.currencyDuty || '');

                    if (matchBaseCurrency === matchTaxCurrency) {
                        totalTaxAmountInBase = totalTaxAmount;
                        taxBaseCurrency = currencyTaxSymbol;
                    } else {
                        totalTaxAmountInBase = totalTaxAmount * totalBoeConversionRate;
                        taxBaseCurrency = baseCurrencySymbol;
                    }

                    if (matchBaseCurrency === matchDutyCurrency) {
                        totalDutyAmountInBase = totalDutyAmount;
                        dutyBaseCurrency = currencyDutySymbol;
                    } else {
                        totalDutyAmountInBase = totalDutyAmount * totalBoeConversionRate;
                        dutyBaseCurrency = baseCurrencySymbol;
                    }

                    const totalShipmentExpense = (proformaAmount * halfConversionRate) + bankCharges + totalExpensesInBase + totalDutyAmountInBase || "";

                    const primaryQty = Number(invoiceData?.particulars?.[0]?.products?.primaryQty || 0);
                    const costPerUnit = totalQty && totalQty !== 0
                        ? ((totalShipmentExpense) / totalQty).toFixed(2)
                        : '0.00';

                    const costPerUnitPrimary = (totalQty && totalQty !== 0 && primaryQty)
                        ? (parseFloat(costPerUnit) / primaryQty).toFixed(2)
                        : '0.00';

                    const secondaryQty = Number(invoiceData?.particulars?.[0]?.products?.secondaryQty || 0);

                    const secondaryUnit = invoiceData?.particulars?.[0]?.products?.secondaryUnitList?.unitName || "";
                    const primaryUnit = invoiceData?.particulars?.[0]?.products?.primaryUnitList?.unitName || "";
                    const productsUnit = invoiceData?.particulars?.[0]?.products?.productsUnit?.unitName || "";

                    const costPerUnitSecondary = (totalQty && totalQty !== 0 && secondaryQty)
                        ? (parseFloat(costPerUnit) / secondaryQty).toFixed(2)
                        : '0.00';

                    const countryColorObj = getColorById(invoiceData?.clientCompany?.billingCountry?.id, colorlist, {
                        matchKey: "colorId",
                        prefixLength: 1,
                    });
                    return {
                        index: (index + 1).toString().padStart(2, '0'),
                        commercialInvoiceId: invoiceData.commercialInvoiceId || '',
                        shipmentId: invoiceData.shipmentId || '',
                        etaDate: companyDateFormat(invoiceData?.date) || '',
                        piNumber: proformaInvoiceNo,
                        clientCompanyName: invoiceData?.clientCompany?.clientCompanyName || "",
                        billingCountry: invoiceData?.clientCompany?.billingCountry?.name || "",
                        // numberOfContainers: invoiceData?.shippingBooking?.reduce((sum, item) => sum + Number(item.numberOfContainers || 0), 0) || 0,
                        totalQty: `${totalQty} ${unitName}`,
                        piAmount: formatAmount(proformaAmount, currencySymbol),
                        piAmountBaseCurrency: formatAmount(proformaAmount * halfConversionRate, baseCurrencySymbol),
                        paymentAmount: formatAmount(advanceAmount, currencySymbol),
                        amountInBaseCurrency: formatAmount(advanceAmountBaseCurrency, baseCurrencySymbol),
                        finalAmount: formatAmount(finalAmount, currencySymbol),
                        finalAmountBaseCurrency: formatAmount(finalAmountBaseCurrency, baseCurrencySymbol),
                        bankCharges: formatAmount(bankCharges, baseCurrencySymbol),
                        shippingLineCharges: formatAmount(shippingLineCharges, currencySymbol),
                        agencyName: invoiceData?.shippingBooking?.map(item => item?.freightForwardAgency?.agencyName).join(", ") || "",
                        chaName: invoiceData?.shippingBooking?.map(item => item?.customClearingAgency?.chaName).join(", ") || "",

                        totalExpenses: formatAmount(totalShipmentExpense, currencySymbol),

                        totalExpensesInBase: formatAmount(totalShipmentExpense, baseCurrencySymbol),
                        totalExpensesWithTax: formatAmount((totalShipmentExpense) + totalTaxAmountInBase, baseCurrencySymbol),

                        seaFreight: formatAmount(seaFreightChargesLedgerStatus, seaFreightSymbol),
                        seaFreightBaseCurrency: formatAmount(seaFreightChargesLedgerBaseCurrency, baseCurrencySymbol),
                        transportCharges: formatAmount(TransportCharges, transportChargesSymbol),
                        transportChargesInBase: formatAmount(TransportChargesInBase, baseCurrencySymbol),

                        cFSCharges: formatAmount(CFSCharges, cfsChargesSymbol),
                        cFSChargesInBase: formatAmount(CFSChargesInBase, baseCurrencySymbol),

                        cHACharges: formatAmount(CHACharges, chaChargesSymbol),
                        cHAChargesInBase: formatAmount(CHAChargesInBase, baseCurrencySymbol),

                        shippingLineandAgencyCharges: formatAmount(ShippingLineandAgencyCharges, shippingLineAgencySymbol),
                        shippingLineandAgencyChargesInBase: formatAmount(ShippingLineandAgencyChargesInBase, baseCurrencySymbol),

                        miscellaneousExpenses: formatAmount(miscellaneousExpenses || 0, currencySymbol),
                        miscellaneousExpensesInBase: formatAmount(miscellaneousExpensesInBase || 0, baseCurrencySymbol),

                        costPerUnit: `${formatAmount(costPerUnit, baseCurrencySymbol)}${productsUnit ? ' ' + productsUnit : ''}`,
                        costPerUnitSecondary: `${formatAmount(costPerUnitSecondary, baseCurrencySymbol)}${secondaryUnit ? ' ' + secondaryUnit : ''}`,
                        costPerUnitPrimary: `${formatAmount(costPerUnitPrimary, baseCurrencySymbol)}${primaryUnit ? ' ' + primaryUnit : ''}`,

                        totalShipmentExpense: formatAmount(totalShipmentExpense || 0, currencySymbol),
                        totalTax: formatAmount(totalTaxAmount, currencyTaxSymbol),
                        totalTaxAmountInBase: formatAmount(totalTaxAmountInBase || 0, taxBaseCurrency),

                        totalDuty: formatAmount(totalDutyAmount, currencyDutySymbol),
                        totalDutyAmountInBase: formatAmount(totalDutyAmountInBase || 0, dutyBaseCurrency),
                        // @@@@@@@@@@@@@@
                        halfConversionRate: halfConversionRate,

                        countryIdColor: countryColorObj.color || '',
                        countryIdBackground: countryColorObj.backgroundColor || '',

                        commercialInvoiceNo: invoiceData?.commercialInvoiceNo || "",

                        bookingNumber: invoiceData?.booking?.bookingNumber || "",

                        numberOfContainers: invoiceData?.booking?.numberOfContainers || 0,


                    };
                });
                setData(mappedData);
                setRowCount(response.data.pagination?.totalRecords || 0);

            } else {
                setError("No commercial invoice data found.");
                setData([]);
            }
        } catch (err) {
            setError(err.message);
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData(page, pageSize)
    }, [page, pageSize, token, colorlist])

    const defaultView = 'importshipmentcostingreport';
    const mainContent = (
        <>
            <CustomDataTable
                title={title}
                columns={columns}
                data={data} handleFilterButton={filterOpen}
                fiterButtonTitile="Fiter"
                rowCount={rowCount}
                page={page}
                setPage={setPage}
                pageSize={pageSize}
                setPageSize={setPageSize}
                fromDate={filterPayload.fromDate || ""}
                toDate={filterPayload.toDate || ""}

            />
            <CustomFilter
                drawerTitle="Filter Options"
                drawerWidth={300}
                openDrawer={openDrawer}
                onClose={filterClose}
                drawerContent={
                    <>
                        <Box>
                            <Grid container spacing={1} sx={{ padding: 0, }}>

                                <Grid item xs={12} sm={12} md={12}>
                                    <CustomAutocomplete
                                        options={suppliersList}
                                        getOptionLabel={(option) => option.title}
                                        placeholder="Select Supplier"
                                        onChange={(event, newValue) => handleFilterChange("supplierNamesId", newValue?.id)}
                                        value={suppliersList.find((item) => item.id === filterPayload.supplierNamesId) || null}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={12} md={12}>
                                    <CustomTextField
                                        fieldLabel="from Date"
                                        type="date"
                                        value={filterPayload.fromDate}
                                        onChange={(e) => handleFilterChange('fromDate', e.target.value)}
                                        placeholder="From Date"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={12} md={12}>
                                    <CustomTextField
                                        fieldLabel="to Date"
                                        type="date"
                                        value={filterPayload.toDate}
                                        onChange={(e) => handleFilterChange('toDate', e.target.value)}
                                        placeholder="To Date"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={12} md={12} sx={{ display: 'flex', alignItems: 'center', marginTop: "10px" }}>
                                    <CustomButton
                                        startIcon={<FindReplaceOutlinedIcon />}
                                        title="Apply"
                                        size="small"
                                        variant="contained"
                                        onClick={fetchData}
                                    />
                                    <CustomButton
                                        variant="outlined"
                                        title="Clear"
                                        startIcon={<ClearRounded />}
                                        onClick={handleClearFilters}
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    </>
                }
            />
        </>

    );

    const getMainContent = (mainContent, permission) => {
        return permission?.view ? mainContent : null;
    };

    return (
        <>
            <CustomReport
                defaultView={defaultView}
                mainContent={getMainContent(mainContent, permission)} />
            {!permission?.view && <AccessDenied />}
        </>

    );
};
export default ImportShipmentCostingReport;
